/* eslint-disable import/extensions */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useRef, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { downloadCSVString } from "../base/components/CSVUtils";
import ErrAlert from "../base/components/ErrAlert";
import Icon from "../base/components/Icon";
import MDText from "../base/components/MDText";
import SubCard from "../base/components/SubCard";
import ServerIO from "../plumbing/ServerIO";

const DSPMeasurePage = (): JSX.Element => {
	const [csvFile, setCSVFile] = useState<File>();
	const [scoredCSV, setScoredCSV] = useState<string>();
	const [alert, setAlert] = useState<{ message: string; color: string }>();

	const schemaRef = useRef<HTMLSelectElement>(null);
	const apiKeyRef = useRef<HTMLInputElement>(null);

	const onDrop = useCallback(async (acceptedFiles: File[], rejectedFile: FileRejection[]) => {
		if (rejectedFile.length > 0) {
			setAlert({
				message: `Do not accept file ${rejectedFile[0].file.name}`,
				color: "danger",
			});
			return;
		}
		setCSVFile(acceptedFiles[0]);
	}, []);

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		accept: ".csv",
	});

	const fetchAPI = async (
		_apiKey: string,
		_schema: string,
		_csvFile: File
	): Promise<string> => {
		const uri = `${ServerIO.API_ENDPOINT}/csv?schema=${_schema}&api_key=${_apiKey}`;
		try {
			setAlert({ message: "Scoring your CSV file...", color: "info" });
			const formData = new FormData();
			formData.append("csv", _csvFile);
			const response = await fetch(uri, {
				method: "POST",
				body: formData,
			});
			if (response.status !== 200) {
				throw Error(await response.text());
			}
			const resData = await response.text();
			return resData;
		} catch (error) {
			console.error("Error:", error);
			setAlert({
				message: `${error}`,
				color: "danger",
			});
			throw error;
		}
	};

	const handleSubmit = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) => {
		e.preventDefault();
		if (!csvFile) return; // Just to be safe
		if (!apiKeyRef.current || apiKeyRef.current.value.length === 0) {
			setAlert({ message: "No API key provided", color: "danger" });
			return;
		}
		if (!schemaRef.current) {
			setAlert({ message: "Schema Error", color: "danger" });
			return;
		}
		const returnedCSV = await fetchAPI(
			apiKeyRef.current.value,
			schemaRef.current.value,
			csvFile
		);
		setScoredCSV(returnedCSV);
		setAlert({ message: "Success!", color: "success" });
	};

	return (
		<>
			<h1>DSP Carbon Mesasure</h1>
			<SubCard title="Instructions to measure carbon footprints using spreadsheet (CSV) report from your DSP">
				<p>
					<Icon name="bug" /> This page is still in development, expect bugs.
				</p>
				<MDText
					source={`
This Page would allow you to call Good-Loop Green CSV Measure Endpoint. You will need to:

1. Prepare a CSV file with all requried columns.
2. An API Key.
3. If it is a report from Meta, decide which schema to use.

---

## Generic report (No schema)

*Keep the schema option empty*

To measure any generic report you will need to have the columns of:

- date
- impressions
- country
- region
- domain
- format
- device

<a href="/data/dspmeasure_example.csv">Download Example CSV</a>

---

## Meta (Facebook)

User can generate csv report from the Ads Manager. But the Breakdown/ Metrics are limited. E.g. Cannot pick the breakdown of Country and Region at the same time. 

In order to use this schema, client must select a Breakdown of:
- Day
- Region
- Media Type (Optional, but required sub-schema input if it's null)

and Metrics of:
- Impressions

**Sub Schema**: If it is a report of only one kind of Media Type (video or display), user can upload a report csv file without the column of Media Type, but listing the media type in sub schema. 

There are two kinds of sub schema: meta-video and meta-display
`}
				/>
			</SubCard>
			<div>
				<span>Schema:</span>{" "}
				<select name="schema" ref={schemaRef}>
					{["", "meta", "meta-video", "meta-display"].map((val, idx) => (
						<option key={idx}>{val}</option>
					))}
				</select>
			</div>
			<div
				className={`text-center mx-5 my-3 p-3 border rounded ${
					csvFile && "border-primary"
				}`}
				{...getRootProps()}
				style={{ cursor: "pointer" }}
			>
				{!csvFile ? (
					<>
						<input {...getInputProps()} />
						<p>Drag & drop a CSV file here, or click to select one</p>
					</>
				) : (
					<p>File selected: {csvFile.name}</p>
				)}
			</div>
			<div id="api_key">
				<label className="mr-1" htmlFor="api_key">
					API Key:
				</label>
				<input className="col-8" id="api_key" ref={apiKeyRef} type="text" />
			</div>
			<button
				type="submit"
				disabled={!csvFile}
				className="btn btn-primary"
				onClick={handleSubmit}
			>
				Submit
			</button>
			<ErrAlert error={alert?.message} color={alert?.color} canClose>
				{alert?.color === "success" && scoredCSV && (
					<>
						<br />
						<button type="submit" onClick={() => downloadCSVString(scoredCSV)}>
							Click here to download your csv file
						</button>
					</>
				)}
			</ErrAlert>
		</>
	);
};

export default DSPMeasurePage;
